import type { FormEvent } from 'react';
import { useState } from 'react';
import type { DominantEmotion, PlanAdherence } from 'shared';

import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DeepReviewCard } from '../review/DeepReviewCard';
import { QuickReviewCard } from '../review/QuickReviewCard';

import { ReviewHistoryPanel } from './ReviewHistoryPanel';
import { usePatchReview } from './usePatchReview';
import { useReviewContext } from './useReviewContext';

export function ReviewDetail({
  tradeId,
  submitted,
}: {
  tradeId: string | null;
  submitted: boolean;
}) {
  const context = useReviewContext(tradeId);
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  if (!tradeId) {
    return <Card className="text-sm text-text-muted">Select a trade review.</Card>;
  }
  if (context.isLoading) return <Card className="text-sm text-text-muted">Loading review...</Card>;
  if (context.isError || !context.data) {
    return <Card className="text-sm text-danger">Could not load this review.</Card>;
  }

  if (!submitted) {
    return context.data.reviewMode === 'quick' ? (
      <QuickReviewCard context={context.data} tradeId={tradeId} />
    ) : (
      <DeepReviewCard context={context.data} tradeId={tradeId} />
    );
  }

  if (editing) {
    return (
      <ReviewEditForm
        tradeId={tradeId}
        mode={context.data.reviewMode}
        onDone={() => setEditing(false)}
      />
    );
  }

  return (
    <Card
      header={
        <div>
          <h2 className="text-lg font-semibold">{context.data.trade.symbol} review</h2>
          <p className="text-sm text-text-muted">Submitted review summary.</p>
        </div>
      }
    >
      <div className="space-y-4">
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-text-muted">Closed</dt>
            <dd>{new Date(context.data.trade.closedAt).toLocaleString()}</dd>
          </div>
          <div>
            <dt className="text-text-muted">Realised P&L</dt>
            <dd>{context.data.trade.realisedPnl}</dd>
          </div>
        </dl>
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => setEditing(true)}>
            Edit
          </Button>
          <Button type="button" variant="secondary" onClick={() => setHistoryOpen((open) => !open)}>
            {historyOpen ? 'Hide history' : 'Edited history'}
          </Button>
        </div>
        {historyOpen ? <ReviewHistoryPanel tradeId={tradeId} /> : null}
      </div>
    </Card>
  );
}

function ReviewEditForm({
  tradeId,
  mode,
  onDone,
}: {
  tradeId: string;
  mode: 'quick' | 'deep';
  onDone: () => void;
}) {
  const patch = usePatchReview();
  const [planAdherence, setPlanAdherence] = useState<PlanAdherence>('followed');
  const [dominantEmotion, setDominantEmotion] = useState<DominantEmotion>('neutral');
  const [oneLineLesson, setOneLineLesson] = useState('');
  const [whatHappened, setWhatHappened] = useState('');
  const [whatToChange, setWhatToChange] = useState('');

  function submit(event: FormEvent) {
    event.preventDefault();
    patch.mutate(
      {
        tradeId,
        body:
          mode === 'quick'
            ? {
                mode,
                planAdherence,
                dominantEmotion,
                ...(oneLineLesson.trim() ? { oneLineLesson: oneLineLesson.trim() } : {}),
              }
            : {
                mode,
                planAdherence,
                dominantEmotion,
                ...(oneLineLesson.trim() ? { oneLineLesson: oneLineLesson.trim() } : {}),
                whatHappened: whatHappened.trim(),
                whatToChange: whatToChange.trim(),
              },
      },
      { onSuccess: onDone },
    );
  }

  const deepValid =
    mode === 'quick' || (whatHappened.trim().length >= 20 && whatToChange.trim().length >= 20);

  return (
    <Card header={<h2 className="text-lg font-semibold">Edit review</h2>}>
      <form className="space-y-4" onSubmit={submit}>
        <ReviewSelect
          label="Plan adherence"
          value={planAdherence}
          onChange={setPlanAdherence}
          values={['followed', 'drifted', 'abandoned']}
        />
        <ReviewSelect
          label="Dominant emotion"
          value={dominantEmotion}
          onChange={setDominantEmotion}
          values={['conviction', 'fomo', 'revenge', 'fear', 'boredom', 'neutral']}
        />
        <ReviewTextarea label="One-line lesson" value={oneLineLesson} onChange={setOneLineLesson} />
        {mode === 'deep' ? (
          <>
            <ReviewTextarea
              label="What happened?"
              value={whatHappened}
              onChange={setWhatHappened}
            />
            <ReviewTextarea
              label="What would you change?"
              value={whatToChange}
              onChange={setWhatToChange}
            />
          </>
        ) : null}
        {patch.isError ? <p className="text-sm text-danger">Could not update review.</p> : null}
        <div className="flex gap-2">
          <Button type="submit" disabled={!deepValid || patch.isPending}>
            Save
          </Button>
          <Button type="button" variant="secondary" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ReviewSelect<TValue extends string>({
  label,
  value,
  values,
  onChange,
}: {
  label: string;
  value: TValue;
  values: TValue[];
  onChange: (value: TValue) => void;
}) {
  return (
    <label className="block text-sm font-medium">
      {label}
      <select
        className="mt-1 h-10 w-full rounded border border-border bg-bg px-3 text-sm"
        value={value}
        onChange={(event) => onChange(event.target.value as TValue)}
      >
        {values.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>
    </label>
  );
}

function ReviewTextarea({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block text-sm font-medium">
      {label}
      <textarea
        className="mt-1 min-h-24 w-full rounded border border-border bg-bg px-3 py-2 text-sm"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
